"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useRef, useState } from "react";
import { Camera, ChevronLeft, Loader2, Lock, Pencil, User } from "lucide-react";
import {
  AccountTabMobileHeader,
  accountTabHeaderPaddingTop,
} from "@/components/account/account-tab-mobile-header";
import { createClient } from "@/utils/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ProfileCoverBand } from "@/components/profile-page/profile-cover-band";
import { useMobile } from "@/hooks/use-mobile";
import { useProfileChromeColors } from "@/hooks/use-profile-chrome-colors";
import { getAvatarUrl } from "@/lib/getAvatarUrl";
import { SQUARE_LG, imagePresetUrl } from "@/lib/images/presets";
import {
  getNormalizedImageFile,
  getSafeImageExtension,
  validateImageFile,
} from "@/lib/image-upload";
import { getModerationDisplayMessage, getModerationReason } from "@/lib/moderation-client";
import { storageBucket, StoragePath } from "@/lib/storage";
import { ROUTE_GUTTER_CLASS } from "@/lib/route-layout";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const BIO_MAX = 150;
const CHECKOUT_NOTE_MAX = 500;

export type ProfileEditUpdates = {
  bio?: string;
  checkoutNote?: string;
  avatarUrl?: string | null;
  bannerUrl?: string | null;
};

type ProfileEditFormProps = {
  userId: string;
  initialUsername: string;
  initialBio: string;
  initialCheckoutNote: string;
  initialAvatarUrl: string | null;
  initialBannerUrl: string | null;
  banned: boolean;
  variant?: "page" | "inline";
  onClose?: () => void;
  onProfileUpdated?: (updates: ProfileEditUpdates) => void;
};

function EditField({
  label,
  description,
  children,
  className,
}: {
  label: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("space-y-2", className)}>
      <div>
        <h2 className="text-sm font-semibold text-foreground">{label}</h2>
        {description ? (
          <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {children}
    </div>
  );
}

export function ProfileEditForm({
  userId,
  initialUsername,
  initialBio,
  initialCheckoutNote,
  initialAvatarUrl,
  initialBannerUrl,
  banned,
  variant = "page",
  onClose,
  onProfileUpdated,
}: ProfileEditFormProps) {
  const t = useTranslations("profile.edit");
  const tInfo = useTranslations("profile.info");
  const tCommon = useTranslations("common");
  const isMobile = useMobile();
  const router = useRouter();
  const isInline = variant === "inline";
  const [username] = useState(initialUsername);
  const [bio, setBio] = useState(initialBio);
  const [checkoutNote, setCheckoutNote] = useState(initialCheckoutNote);
  const [initialBioRef, setInitialBioRef] = useState(initialBio);
  const [initialNoteRef, setInitialNoteRef] = useState(initialCheckoutNote);
  const [avatarUrl, setAvatarUrl] = useState(initialAvatarUrl);
  const [bannerUrl, setBannerUrl] = useState(initialBannerUrl);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const bannerBeforePickRef = useRef<string | null>(null);

  const { coverPlaceholderReady } = useProfileChromeColors({
    avatarUrl,
    bannerUrl,
  });

  const dirty =
    bio.trim() !== initialBioRef.trim() ||
    checkoutNote.trim() !== initialNoteRef.trim();

  const handleBack = () => {
    if (isInline && onClose) {
      onClose();
      return;
    }
    if (typeof window !== "undefined" && window.history.length > 1) {
      router.back();
      return;
    }
    router.push(`/${username}`);
  };

  const uploadImage = async (
    file: File,
    kind: "avatar" | "banner",
  ): Promise<string | null> => {
    const validation = validateImageFile(file);
    if (!validation.valid) {
      toast.error(validation.error ?? "Invalid image file.");
      return null;
    }

    let normalizedFile = file;
    let previewUrl: string | null = null;
    try {
      const normalized = await getNormalizedImageFile(file);
      normalizedFile = normalized.file;
      previewUrl = normalized.previewUrl;
    } catch {
      toast.error("Failed to process the image.");
      return null;
    }

    const supabase = createClient();
    const fileExt = getSafeImageExtension(normalizedFile);
    const sanitizedExt = fileExt ? fileExt.replace(/[^a-z0-9]/g, "") : "jpg";
    const filePath =
      kind === "avatar"
        ? StoragePath.avatar(userId, sanitizedExt)
        : StoragePath.banner(userId, sanitizedExt);

    const { error: uploadError } = await storageBucket(supabase).upload(filePath, normalizedFile, {
      upsert: true,
    });
    if (uploadError) {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      toast.error("Upload failed.");
      return null;
    }

    // Bucket-relative path -> profiles.avatar_url / banner_url (the route
    // resolves it for moderation; read funnels resolve it for render).
    // @see .tondocs/images/HANDOFF_RELATIVE_STORAGE_PATHS_2026_07_27.md §3 L4
    const publicUrl: string | undefined = filePath;
    if (!publicUrl) {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      toast.error("Could not resolve image URL.");
      return null;
    }

    const endpoint = kind === "avatar" ? "/api/profile/avatar" : "/api/profile/banner";
    const response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ imageUrl: publicUrl, storagePath: filePath }),
    });

    if (previewUrl) URL.revokeObjectURL(previewUrl);

    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      const message = payload?.error ?? "Failed to update profile image.";
      if (response.status === 422) {
        toast.error(getModerationReason(getModerationDisplayMessage(message).message) ?? message);
      } else {
        toast.error(message);
      }
      return null;
    }

    const payload = await response.json().catch(() => ({}));
    return kind === "avatar"
      ? (payload.avatarUrl as string | undefined) ?? publicUrl
      : (payload.bannerUrl as string | undefined) ?? publicUrl;
  };

  const handleAvatarPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || banned) return;
    setUploadingAvatar(true);
    const url = await uploadImage(file, "avatar");
    if (url) setAvatarUrl(url);
    setUploadingAvatar(false);
  };

  const handleBannerPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || banned) return;
    bannerBeforePickRef.current = bannerUrl;
    const localPreview = URL.createObjectURL(file);
    setBannerUrl(localPreview);
    setUploadingBanner(true);
    const url = await uploadImage(file, "banner");
    URL.revokeObjectURL(localPreview);
    if (url) {
      setBannerUrl(url);
    } else {
      setBannerUrl(bannerBeforePickRef.current);
    }
    setUploadingBanner(false);
  };

  const handleSave = async () => {
    if (banned) {
      toast.error("Your account has been suspended. Please contact support.");
      return;
    }
    setSaving(true);
    try {
      const trimmedBio = bio.trim().slice(0, BIO_MAX);
      if (trimmedBio !== initialBioRef.trim()) {
        const response = await fetch("/api/profile/bio", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ bio: trimmedBio }),
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => null);
          throw new Error(payload?.error ?? t("bioError"));
        }
        setInitialBioRef(trimmedBio);
        setBio(trimmedBio);
      }

      const trimmedNote = checkoutNote.trim().slice(0, CHECKOUT_NOTE_MAX);
      if (trimmedNote !== initialNoteRef.trim()) {
        const response = await fetch("/api/profile/checkout-note", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ checkoutNote: trimmedNote }),
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => null);
          throw new Error(payload?.error ?? t("storeNoticeError"));
        }
        setInitialNoteRef(trimmedNote);
        setCheckoutNote(trimmedNote);
      }

      toast.success(t("saveSuccess"));
      onProfileUpdated?.({
        bio: trimmedBio,
        checkoutNote: trimmedNote,
        avatarUrl,
        bannerUrl,
      });
      handleBack();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("saveError"));
    } finally {
      setSaving(false);
    }
  };

  const resolvedAvatar = getAvatarUrl(avatarUrl);

  return (
    <>
      {isMobile ? (
        <AccountTabMobileHeader
          title={t("title")}
          onBackAction={handleBack}
          backAriaLabel={tCommon("back")}
        />
      ) : null}

      {isInline && !isMobile ? (
        <div className="mb-4 flex items-center gap-2">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full hover:bg-secondary/50 active:scale-95"
            aria-label={tInfo("goBack")}
          >
            <ChevronLeft className="h-5 w-5 text-foreground" />
          </button>
          <h2 className="min-w-0 truncate text-sm font-semibold text-foreground">{t("title")}</h2>
        </div>
      ) : null}

      <div
        className={cn(
          "mx-auto w-full max-w-(--route-max-width,64rem) pb-[max(1.5rem,env(safe-area-inset-bottom))] md:py-6 md:pb-8",
          isInline ? "px-0 pb-6" : cn(ROUTE_GUTTER_CLASS, !isMobile && "pt-14"),
        )}
        style={
          isMobile
            ? { paddingTop: accountTabHeaderPaddingTop(isInline ? "0.5rem" : "0.75rem") }
            : undefined
        }
      >
        <div className="mb-8 hidden md:flex md:items-start md:gap-3">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="mt-1 h-10 w-10 shrink-0 rounded-full hover:bg-muted"
            onClick={handleBack}
            aria-label="Go back"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-3xl font-bold tracking-tight">{t("title")}</h1>
            <p className="mt-1 text-sm text-muted-foreground">@{username}</p>
          </div>
        </div>

        <div className="relative">
          <input
            ref={bannerInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleBannerPick}
            disabled={uploadingBanner || banned}
          />
          <button
            type="button"
            className="group relative block w-full text-left"
            onClick={() => bannerInputRef.current?.click()}
            disabled={uploadingBanner || banned}
            aria-label={t("coverA11y")}
          >
            <ProfileCoverBand
              bannerUrl={bannerUrl}
              placeholderGradientReady={coverPlaceholderReady}
              className="rounded-2xl md:h-44"
            />
            <span className="absolute inset-0 hidden rounded-2xl bg-black/0 transition-colors group-hover:bg-black/20 md:block" />
            <span className="absolute top-2.5 right-2.5 flex h-9 w-9 items-center justify-center gap-1.5 rounded-full border border-border/60 bg-background/95 text-foreground shadow-sm backdrop-blur-sm md:w-auto md:px-3">
              {uploadingBanner ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Camera className="h-4 w-4 shrink-0" />
                  <span className="hidden text-xs font-medium md:inline">{t("coverA11y")}</span>
                </>
              )}
            </span>
          </button>
        </div>

        <input
          ref={avatarInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleAvatarPick}
          disabled={uploadingAvatar || banned}
        />
        <div className="relative z-10 -mt-10 mb-5 flex justify-end md:-mt-12">
          <button
            type="button"
            className="group/avatar relative"
            onClick={() => avatarInputRef.current?.click()}
            disabled={uploadingAvatar || banned}
            aria-label={t("avatarA11y")}
          >
            <div
              className={cn(
                "relative h-24 w-24 overflow-hidden rounded-full border-[3px] border-background shadow-md md:h-28 md:w-28",
                // White backdrop so transparent avatar PNGs composite on white in both themes.
                resolvedAvatar && !uploadingAvatar ? "bg-white" : "bg-muted",
              )}
            >
              {uploadingAvatar ? (
                <div className="flex h-full w-full items-center justify-center">
                  <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
                </div>
              ) : resolvedAvatar ? (
                // `h-24`/`md:h-28` = 96/112 CSS px -> SQUARE_LG (256), which
                // covers both at >=2x. The previous rung (192) served the 112 px
                // case at 1.71x; the 2026-07-28 preset merge closed that gap as
                // a side effect of coarsening the table.
                // A `blob:` preview during upload passes through untouched.
                <Image src={imagePresetUrl(resolvedAvatar, SQUARE_LG)} alt="" fill className="object-cover" sizes="112px" />
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <User className="h-10 w-10 text-muted-foreground" />
                </div>
              )}
            </div>
            {!uploadingAvatar ? (
              <span className="absolute -bottom-0.5 -right-0.5 flex h-8 w-8 items-center justify-center rounded-full border-2 border-background bg-background shadow-sm transition-colors group-hover/avatar:bg-muted md:h-9 md:w-9">
                <Pencil className="h-3.5 w-3.5 text-foreground md:h-4 md:w-4" />
              </span>
            ) : null}
          </button>
        </div>

        <div className="space-y-6">
          <EditField label={t("usernameLabel")} description={t("usernameLocked")}>
            <div className="relative">
              <Input
                id="profile-edit-username"
                value={`@${username}`}
                readOnly
                className="rounded-full bg-muted/40 pr-10 text-[16px] leading-normal"
              />
              <Lock className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            </div>
          </EditField>

          <EditField label={t("bioLabel")} description={t("bioHelp")}>
            <Textarea
              id="profile-edit-bio"
              value={bio}
              onChange={(e) => setBio(e.target.value.slice(0, BIO_MAX))}
              placeholder={t("bioPlaceholder")}
              rows={4}
              disabled={saving || banned}
              className="min-h-[112px] resize-none rounded-2xl text-[16px] leading-normal"
            />
            <p className="text-right text-xs tabular-nums text-muted-foreground">
              {bio.length}/{BIO_MAX}
            </p>
          </EditField>

          <EditField label={t("storeNoticeLabel")} description={t("storeNoticeHelp")}>
            <Textarea
              id="profile-edit-store-notice"
              value={checkoutNote}
              onChange={(e) => setCheckoutNote(e.target.value.slice(0, CHECKOUT_NOTE_MAX))}
              placeholder={t("storeNoticePlaceholder")}
              rows={5}
              disabled={saving || banned}
              className="min-h-[140px] resize-none rounded-2xl text-[16px] leading-normal"
            />
            <p className="text-right text-xs tabular-nums text-muted-foreground">
              {checkoutNote.length}/{CHECKOUT_NOTE_MAX}
            </p>
          </EditField>

          <Button
            type="button"
            className="h-11 w-full rounded-full text-base font-semibold md:w-auto md:min-w-44 md:px-8"
            onClick={handleSave}
            disabled={saving || uploadingAvatar || uploadingBanner || banned || !dirty}
          >
            {saving ? t("saving") : t("save")}
          </Button>
        </div>
      </div>
    </>
  );
}
